import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, Renderer2, TemplateRef, ViewChild, ViewContainerRef } from '@angular/core';
import { TemplatePortal } from '@angular/cdk/portal';
import { ConnectedPosition, Overlay, OverlayRef, PositionStrategy } from '@angular/cdk/overlay';
import { animate, style, transition, trigger } from '@angular/animations';
import { BehaviorSubject, Observable, of, Subscription } from 'rxjs';
import { catchError, filter, finalize, tap } from 'rxjs/operators';
import { SvcAppSettings } from 'projects/lib-shared-common/src/public-api';
import { UserService } from 'projects/lib-shared-core/src/public-api';
import { SvcReactionService, ReactionsParamsParent } from './svc-reaction.service';
import { CustomReactionImageAnimationSettings, ReactionType, ReactionTypeEnum } from './models/reaction-type.model';
import { Reacted } from './models/reacted.model';
import { SvcReactionChangeEvent } from './models/reaction-events.model';
import { ReactionRelativeFromElement } from './svc-reaction-animation/svc-reaction-animation.component';

const DEFAULT_ANIMATION_TIME = 2000;
const CUSTOM_ANIMATION_TIME = 1500;

@Component({
  selector: 'svc-reaction',
  templateUrl: './svc-reaction.component.html',
  styleUrls: ['./svc-reaction.component.scss'],
  animations: [trigger('fadeOut', [transition(':leave', [animate('200ms', style({ opacity: 0, transform: 'scale(0.7)' }))])])],
  host: {
    '[attr.role]': `'button'`,
    '[attr.tabIndex]': '0',
    '[class.cursor-default]': `disabled`,
  },
})
export class SvcReactionComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('reactionsTemplate') reactionsTemplate!: TemplateRef<unknown>;

  @Input() public registryUniqueId: number | string;
  @Input() public disabled: boolean = false;
  @Input() public position: 'left' | 'right' | 'above' | 'below' = 'above';
  @Input() public applicationId: string;
  @Input() public dontEmitFirstTime: boolean = false;
  @Input() public siteId: number;
  @Input() public parentApplicationId: string;
  @Input() public parentRegisterId: number | string;
  @Input() public parentRegisterUId: string;
  @Input() public parentOriginatorId: string;
  @Input() public parentRegisterCode: number;
  @Input() public parentRegistryCode: number;
  @Input() public relativeFromElement: ReactionRelativeFromElement | ReactionRelativeFromElement[];
  @Input() public registryUniqueUId: string;

  /*** @deprecated Use `position` instead */
  @Input() public positionX: 'left' | 'right' | 'center' = 'center';
  /*** @deprecated Use `position` instead */
  @Input() public positionY: 'top' | 'bottom' = 'top';

  @Output() public onLoadingChange = new EventEmitter<boolean>();
  @Output() public onReactionChange = new EventEmitter<SvcReactionChangeEvent>();

  public loadingList: boolean = false;
  public loadingCurrent: boolean = false;
  public reactionsType: ReactionType[] = [];
  public currentReactionType: ReactionType;
  public hasReacted: boolean = false;

  private _requestSubscription: Subscription;
  private _overlayRef?: OverlayRef;
  private closeTimeout: any;
  private triggerListenerCleanup: (() => void)[] = [];
  private isMouseOverMenu = false;
  private currentReactionChanged$: BehaviorSubject<{ success: boolean }>;
  public showAnimation = false;
  public currentCustomReactionAnimation: CustomReactionImageAnimationSettings;
  private animatedTimeout;

  constructor(
    private _appSettings: SvcAppSettings,
    private _service: SvcReactionService,
    private _userService: UserService,
    private overlay: Overlay,
    private elementRef: ElementRef<HTMLElement>,
    private viewContainerRef: ViewContainerRef,
    private renderer: Renderer2,
    private _cdr: ChangeDetectorRef
  ) {}

  public ngOnInit(): void {
    this.#loadReaction(true);
  }

  public ngAfterViewInit(): void {
    const triggerElement = this.elementRef.nativeElement;
    const positionStrategy = this.#getPositionStrategy();

    this._overlayRef = this.overlay.create({
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.close(),
    });

    this.triggerListenerCleanup.push(
      this.renderer.listen(triggerElement, 'mouseenter', () => {
        if (!this.showAnimation) {
          this.isMouseOverMenu = true;
          this.#showMenu();
        }
      })
    );
    this.triggerListenerCleanup.push(
      this.renderer.listen(triggerElement, 'mouseleave', () => {
        this.isMouseOverMenu = false;
        this.#scheduleMenuClose();
      })
    );
  }

  public ngOnDestroy(): void {
    this._requestSubscription?.unsubscribe();
    this.triggerListenerCleanup.forEach((cleanup) => cleanup());
    clearTimeout(this.closeTimeout);
    this._overlayRef.dispose();
  }

  #loadReaction(itIsFirstTime: boolean = false): void {
    this.loadingList = true;
    const emit = !itIsFirstTime || !this.dontEmitFirstTime;
    emit && this.onLoadingChange.emit(true);
    this._requestSubscription = this._service
      .getReactionsType()
      .pipe(
        tap((list: ReactionType[]) => {
          this.reactionsType = list;

          if (this.registryUniqueId) {
            const currentRequest =
              typeof this.registryUniqueId == 'number'
                ? this._service.getIdTotalReaction({
                    applicationId: this.applicationId ?? this._appSettings.applicationId,
                    registryUniqueId: this.registryUniqueId,
                    siteId: this.siteId ?? this._userService.user.lastSiteId,
                  })
                : this._service.getUidTotalReaction({
                    applicationId: this.applicationId ?? this._appSettings.applicationId,
                    registryUniqueUId: this.registryUniqueId,
                    siteId: this.siteId ?? this._userService.user.lastSiteId,
                  });
            this.loadingCurrent = true;
            emit && this.onLoadingChange.emit(true);
            this._requestSubscription = currentRequest
              .pipe(
                catchError(() => of([] as Reacted[])),
                tap((totalReactions?: Reacted[]) => {
                  const current = totalReactions?.find((x) => x.userHasReacted);
                  if (current) {
                    const reactionType = this.reactionsType.find((x) => x.reactionUniqueId === current.reactionUniqueId);
                    this.#changeReactionType(reactionType, { emit });
                  } else {
                    this.#changeReactionType(null, { emit });
                  }
                }),
                finalize(() => {
                  this.loadingCurrent = false;
                  emit && this.onLoadingChange.emit(false);
                  this._cdr.markForCheck();
                })
              )
              .subscribe();
          }
        }),
        finalize(() => {
          this.loadingList = false;
          this._cdr.markForCheck();
        })
      )
      .subscribe();
  }

  public refresh(): void {
    this.#loadReaction();
  }

  public showPicker(): void {
    if (!this.showAnimation) {
      clearTimeout(this.closeTimeout);
      this.isMouseOverMenu = true;
      this.#showMenu();
    }
  }

  public makeReactionByEnum(reactionTypeEnum: ReactionTypeEnum, options?: { dispatchLoading?: boolean; anticipateAction?: boolean }): void {
    if (this.disabled) return;
    options = options ?? { anticipateAction: true, dispatchLoading: false };
    const reactionType = this.reactionsType.find((x) => x.enumKey == reactionTypeEnum);
    if (reactionType) {
      this.makeReactionByClick(reactionType, options);
    }
  }

  public makeReactionByClick(reactionType: ReactionType, options?: { dispatchLoading?: boolean; anticipateAction?: boolean }): void {
    if (this.disabled) return;
    this.makeReaction(reactionType, options);

    if (!this.currentReactionType) return;

    if (this.elementRef?.nativeElement) {
      this.showAnimation = true;
      this._cdr.markForCheck();
      this._overlayRef.dispose();
      clearTimeout(this.animatedTimeout);
      this.animatedTimeout = setTimeout(() => {
        this.showAnimation = false;
        this._cdr.markForCheck();
      }, this.currentCustomReactionAnimation.time);
      return;
    }

    this._overlayRef.dispose();
    clearTimeout(this.animatedTimeout);
  }

  public async makeReaction(reactionType: ReactionType, options?: { dispatchLoading?: boolean; anticipateAction?: boolean }): Promise<void> {
    if (this.disabled) return;
    options = options ?? { anticipateAction: true, dispatchLoading: false };
    if (reactionType.reactionUniqueId == this.currentReactionType?.reactionUniqueId) {
      this.removeCurrentReaction({
        dispatchLoading: options?.dispatchLoading,
        anticipateAction: options?.anticipateAction,
      });
    } else {
      const startChanging = (): Observable<void> => (this.currentReactionChanged$ = new BehaviorSubject(null));
      const notifyChanging = (success: boolean = true): void => {
        this.currentReactionChanged$.next({ success });
        this.currentReactionChanged$ = null;
      };
      this._requestSubscription?.unsubscribe();
      const currentRactionType = this.currentReactionType;
      if (options?.anticipateAction ?? false) {
        this.#hideMenu();
        startChanging();
        this.#changeReactionType(reactionType, { wasAticipated: true });
      }
      if (currentRactionType) {
        try {
          await this.removeCurrentReaction({
            current: currentRactionType,
            dispatchChange: false,
            dispatchLoading: options?.dispatchLoading,
          });
        } catch {
          if (options?.anticipateAction ?? false) {
            startChanging();
            this.#changeReactionType(currentRactionType, { wasAticipated: true });
            notifyChanging(false);
          }
          return;
        }
      }
      this.loadingCurrent = true;
      this._cdr.markForCheck();
      if (options?.dispatchLoading ?? true) {
        this.onLoadingChange.emit(true);
      }
      const parent = this.#getReactionsParamsParent();
      const request =
        typeof this.registryUniqueId == 'number'
          ? this._service.reactionId({
              siteId: this.siteId ?? this._userService.user.lastSiteId,
              applicationId: this.applicationId ?? this._appSettings.applicationId,
              reactionUniqueId: reactionType.reactionUniqueId,
              registryUniqueId: this.registryUniqueId,
              ...(this.registryUniqueUId ? { registryUniqueUId: this.registryUniqueUId } : {}),
              ...(parent && { parent }),
            })
          : this._service.reactionUid({
              siteId: this.siteId ?? this._userService.user.lastSiteId,
              applicationId: this.applicationId ?? this._appSettings.applicationId,
              reactionUniqueId: reactionType.reactionUniqueId,
              registryUniqueUId: this.registryUniqueId,
              ...(parent && { parent }),
            });
      this._requestSubscription = request
        .pipe(
          tap(() => {
            if (!(options?.anticipateAction ?? false)) {
              this.#changeReactionType(reactionType);
            } else {
              notifyChanging();
            }
          }),
          catchError((error) => {
            const wasAticipated = options?.anticipateAction ?? false;
            wasAticipated && startChanging();
            this.#changeReactionType(currentRactionType, { wasAticipated });
            wasAticipated && notifyChanging(false);
            return error;
          }),
          finalize(() => {
            this._requestSubscription = null;
            this.loadingCurrent = false;
            this.#hideMenu();
            if (options?.dispatchLoading ?? true) {
              this.onLoadingChange.emit(false);
            }
            this._cdr.markForCheck();
          })
        )
        .subscribe();
    }
  }

  public removeCurrentReaction(options?: { current?: ReactionType; dispatchChange?: boolean; dispatchLoading?: boolean; anticipateAction?: boolean }): Promise<void> {
    if (this.disabled) return;
    const startChanging = (): Observable<void> => (this.currentReactionChanged$ = new BehaviorSubject(null));
    const notifyChanging = (success: boolean = true): void => {
      this.currentReactionChanged$.next({ success });
      this.currentReactionChanged$ = null;
    };
    options = options ?? { anticipateAction: true, dispatchLoading: false };
    const currentRactionType = 'current' in options ? options.current : this.currentReactionType;
    this._requestSubscription?.unsubscribe();
    return new Promise<void>((resolve, reject) => {
      this.loadingCurrent = true;
      this._cdr.markForCheck();
      if ((options?.dispatchChange ?? true) && (options?.anticipateAction ?? false)) {
        this.#hideMenu();
        startChanging();
        this.#changeReactionType(null, { wasAticipated: true });
      }
      if (options?.dispatchLoading ?? true) {
        this.onLoadingChange.emit(true);
      }
      const request =
        typeof this.registryUniqueId == 'number'
          ? this._service.removeIdReaction({
              siteId: this.siteId ?? this._userService.user.lastSiteId,
              applicationId: this.applicationId ?? this._appSettings.applicationId,
              reactionUniqueId: currentRactionType.reactionUniqueId,
              registryUniqueId: this.registryUniqueId,
            })
          : this._service.removeUidReaction({
              siteId: this.siteId ?? this._userService.user.lastSiteId,
              applicationId: this.applicationId ?? this._appSettings.applicationId,
              reactionUniqueId: currentRactionType.reactionUniqueId,
              registryUniqueUId: this.registryUniqueId,
            });
      this._requestSubscription = request
        .pipe(
          tap(() => {
            if (options?.dispatchChange ?? true) {
              if (!(options?.anticipateAction ?? false)) {
                this.#changeReactionType(null);
              } else {
                notifyChanging();
              }
            }
            resolve();
          }),
          catchError((error) => {
            if (options?.dispatchChange ?? true) {
              this.#changeReactionType(currentRactionType, { wasAticipated: options?.anticipateAction ?? false });
              if (options?.anticipateAction ?? false) {
                notifyChanging(false);
              }
            }
            reject(error);
            return error;
          }),
          finalize(() => {
            this._requestSubscription = null;
            this.loadingCurrent = false;
            this.#hideMenu();
            if (options?.dispatchLoading ?? true) {
              this.onLoadingChange.emit(false);
            }
            this._cdr.markForCheck();
          })
        )
        .subscribe();
    });
  }

  #getReactionsParamsParent(): ReactionsParamsParent {
    if (this.parentApplicationId && this.parentOriginatorId && (this.parentRegisterId != null || this.parentRegisterUId)) {
      const parent: ReactionsParamsParent = {
        parentApplicationId: this.parentApplicationId,
        parentOriginatorId: this.parentOriginatorId,
      };

      if (this.parentRegisterId != null) {
        if (typeof this.parentRegisterId === 'number') parent.parentRegisterId = this.parentRegisterId;
        else parent.parentRegisterUId = this.parentRegisterId;
      }

      if (this.parentRegisterUId) parent.parentRegisterUId = this.parentRegisterUId;
      if (this.parentRegisterCode != null) parent.parentRegisterCode = this.parentRegisterCode;
      if (this.parentRegistryCode != null) parent.parentRegistryCode = this.parentRegistryCode;

      return parent;
    }

    return null;
  }

  #applyCurrentCustomReactionAnimation(reactionType: ReactionType): void {
    const settings = reactionType?.customReactionImageAnimationSettings ?? <CustomReactionImageAnimationSettings>{};
    if (settings.type === 'CUSTOM') {
      this.currentCustomReactionAnimation = <CustomReactionImageAnimationSettings>{
        ...(settings ?? {}),
        time: CUSTOM_ANIMATION_TIME,
        animationOriginX: `${settings.animationOriginX}px`,
        animationOriginY: `${settings.animationOriginY}px`,
      };
    } else if (settings.type === 'DEFAULT') {
      this.currentCustomReactionAnimation = {
        animationOriginX: '50%',
        animationOriginY: '50%',
        imageUrl: reactionType.animatedImageUrl,
        scale: 0.16,
        sizeHeight: 150,
        sizeWidth: 150,
        time: DEFAULT_ANIMATION_TIME,
        type: 'DEFAULT',
      };
    }
    if (this.currentCustomReactionAnimation) {
      this.currentCustomReactionAnimation.opacity = '0';
      this.currentCustomReactionAnimation.fullScale = `scale(${this.currentCustomReactionAnimation.scale})`;
      setTimeout(() => {
        this.currentCustomReactionAnimation.opacity = `1`;
        this.currentCustomReactionAnimation.fullScale = `scale(1)`;
        this._cdr.markForCheck();
      });
    }
  }

  #changeReactionType(reactionType: ReactionType, options?: { emit?: boolean; wasAticipated?: boolean }): void {
    const currentReactionType = this.currentReactionType;
    this.currentReactionType = reactionType;
    this.#applyCurrentCustomReactionAnimation(reactionType);
    this.hasReacted = !!reactionType;
    this._cdr.markForCheck();
    (options?.emit ?? true) &&
      this.onReactionChange.emit({
        reactionType,
        wasAdded: !currentReactionType,
        wasRemoved: !reactionType,
        wasChanged: reactionType?.enumKey != currentReactionType?.enumKey,
        anticipated:
          (options?.wasAticipated ?? false)
            ? {
                targetReactionType: reactionType,
                currentReactionType: currentReactionType,
                onReactionDone$: this.currentReactionChanged$.pipe(filter((response) => !!response)),
              }
            : undefined,
      });
  }

  #getPositionStrategy(): PositionStrategy {
    let connectedPosition: ConnectedPosition;

    if (['below', 'above'].includes(this.position)) {
      connectedPosition = {
        originX: 'center',
        originY: this.position == 'below' ? 'bottom' : 'top',
        overlayX: 'center',
        overlayY: this.position == 'below' ? 'top' : 'bottom',
        offsetY: this.position == 'above' ? -5 : 5,
      };
    } else if (['left', 'right'].includes(this.position)) {
      connectedPosition = {
        originX: this.position == 'left' ? 'start' : 'end',
        originY: 'center',
        overlayX: this.position == 'left' ? 'end' : 'start',
        overlayY: 'center',
        offsetX: this.position == 'left' ? -5 : 5,
      };
    }
    return this.overlay.position().flexibleConnectedTo(this.elementRef?.nativeElement).withPositions([connectedPosition]);
  }

  #showMenu(): void {
    clearTimeout(this.closeTimeout);

    if (!this._overlayRef.hasAttached()) {
      const positionStrategy = this.#getPositionStrategy();

      this._overlayRef = this.overlay.create({
        positionStrategy,
        scrollStrategy: this.overlay.scrollStrategies.close(),
      });
    }

    if (this._overlayRef.hasAttached()) return;
    this._overlayRef.attach(new TemplatePortal(this.reactionsTemplate, this.viewContainerRef));
    const overlayElement = this._overlayRef.overlayElement;

    this.triggerListenerCleanup.push(
      this.renderer.listen(overlayElement, 'mouseenter', () => {
        this.isMouseOverMenu = true;
        this.#cancelMenuClose();
      })
    );
    this.triggerListenerCleanup.push(
      this.renderer.listen(overlayElement, 'mouseleave', () => {
        this.isMouseOverMenu = false;
        this.#scheduleMenuClose();
      })
    );
  }

  #hideMenu(): void {
    this._overlayRef.detach();
  }

  #scheduleMenuClose(): void {
    this.closeTimeout = setTimeout(() => {
      if (!this.isMouseOverMenu) this.#hideMenu();
    }, 200);
  }

  #cancelMenuClose(): void {
    clearTimeout(this.closeTimeout);
  }
}
